/* eslint-disable @typescript-eslint/naming-convention */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import Config from '@config/config';
import { AuthToken } from '@services/auth-token/authToken';
import { OfficeAuthToken } from '@services/auth-token/officeAuthToken';
import { queryBuilder } from '@services/url/url';
import { OfficeLoginRefreshPayload } from '@shared/interfaces/backoffice';

import { BaseClient } from './_baseClient';

const singleton         = Symbol();
const singletonEnforcer = Symbol();
const { api }           = Config;
const apiVersion        = 'v1';

class CSocialMediaAPIClient extends BaseClient {
    protected _dashboardEnv = '';
    protected _demoClientName = 'demo';

    private constructor(enforcer) {
        super();

        try {
            if (enforcer !== singletonEnforcer) {
                throw new Error('Cannot construct singleton');
            }

            this._dashboardEnv = AuthToken.getDashboardEnvironment();
            this._baseURL      = `${api.backofficeApiDomain}/api/${apiVersion}`;
            this._config       = {
                headers: {
                    'Content-Type': 'application/ld+json',
                    Authorization: `Bearer ${OfficeAuthToken.getToken()}`,
                }
            };
            this._session      = axios.create({ withCredentials: false, baseURL: this._baseURL });
        } catch (e: unknown) {
            console.error(e);
        }
    }

    initConfig() {
        this._initSession();
    }

    protected _initSession() {
        this._dashboardEnv = AuthToken.getDashboardEnvironment();
        this._baseURL      = `${api.backofficeApiDomain}/api/${apiVersion}`;
        this._config       = {
            headers: {
                'Content-Type': 'application/ld+json',
                Authorization: OfficeAuthToken.getToken() ? `Bearer ${OfficeAuthToken.getToken()}` : '',
            }
        };

        this._session      = axios.create({ withCredentials: false, baseURL: this._baseURL });
        this._session.interceptors.response.use(
            (response) => response,
            (error: { response: Record<string, any> }) => {
                if (axios.isCancel(error)) {
                    console.log('[interceptors.response]: request canceled');

                    return;
                }

                return Promise.reject(error);
            }
        );
    }

    static get instance(): CSocialMediaAPIClient & AxiosInstance {
        // Try to get an efficient singleton
        if (!this[singleton]) {
            this[singleton] = new CSocialMediaAPIClient(singletonEnforcer);
        }

        return this[singleton];
    }

    refreshToken<AxiosReturn = any>(data: OfficeLoginRefreshPayload, config?: AxiosRequestConfig) {
        return this.post<AxiosReturn>(`${api.backofficeApiDomain}/api/token/refresh`, data, { ...this._config, ...config, withCredentials: false });
    }
    protected handleRefreshCheck() {
        const token        = OfficeAuthToken.getToken();
        const tokenHandler = new OfficeAuthToken(token);
        const refreshToken = OfficeAuthToken.getRefreshToken();

        if (!tokenHandler.isExpired && tokenHandler.isValid) {
            return;
        }

        if (!refreshToken) {
            return;
        }

        return (
            this.refreshToken({
                refresh_token: refreshToken,
            })
                .then((response) => {
                    if (!response.data) {
                        throw new Error('Failed to refresh token');
                    }

                    OfficeAuthToken.storeToken(response.data.token, response.data.refresh_token);
                    this._config  = {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: OfficeAuthToken.getToken() ? `Bearer ${OfficeAuthToken.getToken()}` : '',
                        }
                    };
                    this._session = axios.create({ withCredentials: true, baseURL: this._baseURL });
                })
                .catch((error) => {
                    console.log('[interceptors.response]: refresh token failed');

                    const message = String(error?.message || error);

                    if (message.includes('401')) {
                        OfficeAuthToken.removeToken();
                        window.location.reload();
                    }
                })
        );
    }

    isDemoClient() {
        const token   = AuthToken.getToken();
        const handler = new AuthToken(token);

        if (handler.decodedToken.site.name === this._demoClientName) {
            return true;
        }

        return false;
    }

    async getActiveProfiles<AxiosReturn = any>(clientId: string, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        // return this.get<AxiosReturn>(`/allSocialMediaClient/${this.isDemoClient() ? this._demoClientName : clientId}`, { ...this._config, ...config });
        return this.get<AxiosReturn>(`/allActiveSocial/${this.isDemoClient() ? this._demoClientName : clientId}`, { ...this._config, ...config });
    }

    async getOverviewEngagement<Params extends Record<string, any> = any, AxiosReturn = any>(params: Params, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        const clientId = params?.client_id ?? 'inkyfada';
        const url      = queryBuilder(`/social_media/engagement/${this.isDemoClient() ? this._demoClientName : clientId}`, params);

        return this.get<AxiosReturn>(url, { ...this._config, ...config });
    }

    async getOverview<Params extends Record<string, any> = any, AxiosReturn = any>(params: Params, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        const { client_id, ...rest } = params;
        const clientId               = this.isDemoClient() ? this._demoClientName : client_id;

        const url = queryBuilder('/social_media/overview', { ...rest, client_id: clientId });

        return this.get<AxiosReturn>(url, { ...this._config, ...config });
    }

    async getSingle<Params extends { id: string; [key: string]: any }, AxiosReturn = any>(
        params: Params, config?: AxiosRequestConfig
    ) {
        await this.handleRefreshCheck();

        const { id, client_id, ...rest } = params;
        const clientId                   = this.isDemoClient() ? this._demoClientName : client_id;
        const url                        = queryBuilder(`/social_media/profile/${id}`, { ...rest, client_id: clientId });

        return this.get<AxiosReturn>(url, { ...this._config, ...config });
    }

    async getAllPosts<Params extends Record<string, any> = any, AxiosReturn = any>(params: Params, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        const { id, ...rest } = params;

        return this.get<AxiosReturn>(`/posts/${id}`, { ...this._config, ...config, params: rest });
    }

    async getPost<Params extends Record<string, any> = any, AxiosReturn = any>(params: Params, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        const { id, ...rest } = params;

        return this.get<AxiosReturn>(`/post/${id}`, { ...this._config, ...config, params: rest });
    }

    async getAllStories<Params extends Record<string, any> = any, AxiosReturn = any>(params: Params, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        const { id, ...rest } = params;

        return this.get<AxiosReturn>(`/stories/${id}`, { ...this._config, ...config, params: rest });
    }

    async getStory<Params extends Record<string, any> = any, AxiosReturn = any>(params: Params, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        const { id, ...rest } = params;

        return this.get<AxiosReturn>(`/story/${id}`, { ...this._config, ...config, params: rest });
    }

    async getProfiles<AxiosReturn = any>(clientId: string, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        // return this.get<AxiosReturn>(`/allActiveSocial/${this.isDemoClient() ? this._demoClientName : clientId}`, { ...this._config, ...config });
        return this.get<AxiosReturn>(`/allSocialMediaClient/${this.isDemoClient() ? this._demoClientName : clientId}`, { ...this._config, ...config });
    }

    async addProfile<AxiosReturn = any, Data = any>(data: Data, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        return this.post<AxiosReturn>('/new_social_media', { ...data, env: this._dashboardEnv }, { ...this._config, ...config });
    }

    async deleteProfile<AxiosReturn = any>(data: { url: string; clientId: string }, config?: AxiosRequestConfig) {
        await this.handleRefreshCheck();

        return this.get<AxiosReturn>(
            `/delete_social_media?clientId=${this.isDemoClient() ? this._demoClientName : data.clientId}&url=${encodeURIComponent((data.url))}`,
            { ...this._config, ...config }
        );
    }
}

export const SocialMediaAPIClient = CSocialMediaAPIClient.instance;
